import get from 'lodash/get';

import { SYSTEM_DISK_NAME } from '../../../capabilities/image-vm/constants';
import type { SystemDiskRecord } from '../../../capabilities/image-vm/types';
import type { SharedVolumeRecord } from '../../../capabilities/shared-volume/types';
import type { DataDiskRecord } from '../../../capabilities/storage/types';
import { resolveStorageClassSubmitValue } from '../../../components/storageClassField';

function formatGi(size: number | string | undefined): string | undefined {
  if (size === undefined || size === null || size === '') return undefined;
  const text = String(size);
  return text.endsWith('Gi') ? text : `${text}Gi`;
}

function resolveDiskStorageClassName(disk: {
  storageClass?: string;
  pricing?: { deviceID?: string; deviceId?: string };
}): string | undefined {
  return resolveStorageClassSubmitValue(disk.storageClass, disk.pricing);
}

interface VmSystemFields {
  architecture?: string;
  os?: string;
  version?: string;
  bootloader?: string;
}

/** 合并系统盘镜像 system 与表单 system；architecture 仅来自资源配置步骤（fromForm） */
export function mergeVmSystem(fromDisk?: VmSystemFields, fromForm?: VmSystemFields): VmSystemFields | undefined {
  if (!fromDisk && !fromForm) return undefined;
  const merged: VmSystemFields = {
    ...fromDisk,
    ...fromForm,
    os: fromForm?.os || fromDisk?.os,
    version: fromForm?.version || fromDisk?.version,
    bootloader: fromForm?.bootloader || fromDisk?.bootloader,
  };
  if (fromForm?.architecture) {
    merged.architecture = fromForm.architecture;
  }
  return merged;
}

export function serializeSystemDiskVolume(disk: SystemDiskRecord | undefined, creator: string) {
  if (!disk) return { volumes: [], system: undefined, image: undefined, source: undefined };

  const diskSize = formatGi(disk.diskSize);
  const image = disk.image;
  const source = image?.source;

  if (disk.sourceKind === 'existing') {
    return {
      volumes: [
        {
          name: SYSTEM_DISK_NAME,
          type: 'System',
          size: diskSize,
          storageClassName: resolveDiskStorageClassName(disk),
          reclaimPolicy: disk.reclaimPolicy,
          source: {
            type: 'Volume',
            externalDisk: {
              name: disk.diskName,
              nickname: disk.diskNickname || disk.diskName,
            },
          },
        },
      ],
      system: image
        ? {
            os: image.os,
            version: image.version,
            bootloader: image.bootloader,
          }
        : undefined,
      image: image?.url,
      source,
    };
  }

  return {
    volumes: [
      {
        name: SYSTEM_DISK_NAME,
        type: 'System',
        size: diskSize,
        storageClassName: resolveDiskStorageClassName(disk),
        reclaimPolicy: disk.reclaimPolicy,
        volumeTemplate: {
          spec: {
            creator,
            name: disk.diskName || SYSTEM_DISK_NAME,
            pvc: {
              resources: {
                requests: {
                  storage: diskSize,
                },
              },
              storageClassName: resolveDiskStorageClassName(disk),
            },
            source: {
              http: {
                url: image?.url,
              },
              type: 'HTTP',
            },
          },
        },
      },
    ],
    system: image
      ? {
          os: image.os,
          version: image.version,
          bootloader: image.bootloader,
        }
      : undefined,
    image: image?.url,
    source,
  };
}

export function serializeDataDiskVolumes(disks: DataDiskRecord[] | undefined, creator: string) {
  if (!Array.isArray(disks) || !disks.length) return [];

  return disks.map((item) => {
    const diskSize = formatGi(item.diskSize) || '1Gi';
    const storageClassName = resolveDiskStorageClassName(item);
    if (item.sourceKind === 'existing') {
      return {
        name: item.diskName,
        type: 'Data',
        size: diskSize,
        storageClassName,
        reclaimPolicy: item.reclaimPolicy,
        source: {
          type: 'Volume',
          externalDisk: {
            name: item.diskName,
            nickname: item.diskNickname || item.diskName,
          },
        },
      };
    }

    return {
      name: item.diskName,
      type: 'Data',
      size: diskSize,
      storageClassName,
      reclaimPolicy: item.reclaimPolicy,
      volumeTemplate: {
        metadata: {},
        spec: {
          creator,
          name: item.diskName,
          pvc: {
            resources: {
              requests: {
                storage: diskSize,
              },
            },
            storageClassName,
          },
          source: {
            blank: {},
            type: 'Blank',
          },
        },
      },
    };
  });
}

export function serializeSharedVolumes(volumes: SharedVolumeRecord[] | undefined) {
  if (!Array.isArray(volumes)) return [];
  return volumes.map((volume) => ({
    ...volume,
    type: get(volume, 'type') || 'Data',
    source: {
      ...(get(volume, 'source') as Record<string, unknown>),
      type: get(volume, 'source.type') || 'FileSystem',
    },
  }));
}

export function flattenDnsNameservers(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object' && 'server' in item) {
        return String((item as { server?: string }).server || '');
      }
      return '';
    })
    .filter(Boolean);
}

export function flattenDnsSearches(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object' && 'search' in item) {
        return String((item as { search?: string }).search || '');
      }
      return '';
    })
    .filter(Boolean);
}

export function toFormDnsNameservers(raw: unknown): { server: string }[] {
  return flattenDnsNameservers(raw).map((server) => ({ server }));
}

export function toFormDnsSearches(raw: unknown): { search: string }[] {
  return flattenDnsSearches(raw).map((search) => ({ search }));
}
